import React, { useEffect, useReducer, useRef, useState, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Button, LoadingShimmer, Modal, useModal } from '@newsela/angelou';
import { useStoreState } from 'easy-peasy';
import PropTypes from 'prop-types';

import { queries } from '@client/common/graph';
import { useRestQuery } from '@client/common/hooks';
import { SUBJECTS_AREA_TO_PRIMARY, Z_INDEX_9999_MAX } from '@client/utils/constants';
import { QUESTION_TYPES } from '@client/utils/create-assessments';
import { isLocalEnv } from '@client/utils/environment';
import * as toast from '@client/utils/toast';

import Divider from './Divider';
import Filters from './Filters';
import Footer from './Footer';
import Header from './Header';
import image from './NoResults.png';
import Pagination from './Pagination';
import ReviewContainer from './ReviewContainer';
import StandardsList from './StandardsList';
import { $modalContentStyles, $modalBody, $conceptsNotFound } from './style';
import { useRecommendations } from './useRecommendations';
import { sortByConfidenceRating, sortCategorizedStandards } from './utils';
import { shouldFetchPredictions } from '../StandardSelect/StandardSelect';

export const TABS = {
  SEARCH: 'search',
  REVIEW: 'review',
};

export const STANDARD_SELECTED = 'STANDARD_SELECTED';
export const STANDARD_APPLIED = 'STANDARD_APPLIED';

const initialState = {
  search: '',
  limitToNational: false,
  onlyCrosswalks: false,
  adoptionYearRange: { from: '', to: '' },
  subjects: [],
  grades: [],
  states: [],
  page: 0,
  pageSize: 20
};

export const SET_SEARCH = 'SET_SEARCH';
export const TOGGLE_LIMIT_TO_NATIONAL = 'TOGGLE_LIMIT_TO_NATIONAL';
export const TOGGLE_ONLY_CROSSWALKS = 'TOGGLE_ONLY_CROSSWALKS';
export const SET_FILTER_RANGE = 'SET_FILTER_RANGE';
export const SET_FILTER_SUBJECTS = 'SET_FILTER_SUBJECTS';
export const SET_FILTER_GRADES = 'SET_FILTER_GRADES';
export const SET_FILTER_STATES = 'SET_FILTER_STATES';
export const SET_PAGE = 'SET_PAGE';
export const SET_PAGE_SIZE = 'SET_PAGE_SIZE';
export const CLEAR_ALL_FILTERS = 'CLEAR_ALL_FILTERS';

function reducer (state, action) {
  switch (action.type) {
    case SET_SEARCH:
      return { ...state, search: action.payload };
    case TOGGLE_LIMIT_TO_NATIONAL:
      return { ...state, limitToNational: !state.limitToNational, page: 0 };
    case TOGGLE_ONLY_CROSSWALKS:
      return { ...state, onlyCrosswalks: !state.onlyCrosswalks, page: 0 };
    case SET_FILTER_RANGE:
      return { ...state, adoptionYearRange: action.payload, page: 0 };
    case SET_FILTER_SUBJECTS:
      return { ...state, subjects: action.payload, page: 0 };
    case SET_FILTER_GRADES:
      return { ...state, grades: action.payload, page: 0 };
    case SET_FILTER_STATES:
      return { ...state, states: action.payload, page: 0 };
    case SET_PAGE:
      return { ...state, page: action.payload };
    case SET_PAGE_SIZE:
      return { ...state, pageSize: action.payload };
    case CLEAR_ALL_FILTERS:
      return {
        ...initialState
      };
    default:
      return state;
  }
}

function generateQueryJson (state, appliedStandardIds = []) {
  const query = {
    metadata_type: ['STANDARD'],
    exclude_ids: appliedStandardIds,
    page: state.page,
    page_size: state.pageSize,
  };
  if (state.search && state.search.trim()) {
    query.needle = state.search.trim();
  }
  if (state.limitToNational) {
    query.is_national = true;
    query.regions = ['US'];
  } else if (!state.states.length) {
    query.include_all_regions = true;
  }

  if (state.onlyCrosswalks) {
    query.only_crosswalks = true;
  }

  if (state.subjects.length) {
    const allSubjectCodes = state.subjects.flatMap(
      (key) => SUBJECTS_AREA_TO_PRIMARY[key] || []
    );
    query.subjects = allSubjectCodes;
  }

  if (state.grades.length) {
    query.grade_levels = state.grades;
  }

  if (state.states.length) {
    query.regions = state.states;
  }

  const from = parseInt(state.adoptionYearRange.from, 10);
  const to = parseInt(state.adoptionYearRange.to, 10);
  if (!isNaN(from) && !isNaN(to) && from <= to) {
    query.adoption_years = [];
    for (let year = from; year <= to; year++) {
      query.adoption_years.push(year);
    }
  }

  return query;
}

const getContentQuery = (data) => {
  if (data.questionType && QUESTION_TYPES.includes(data.questionType)) {
    return queries.assessmentQuestion;
  }
  return queries.fullContent;
};

// Does not point to the monolith proxy when running locally, since it would require more services to be running.
const MONOLITH_BASE_URL = isLocalEnv(window.location.hostname) ? 'https://app.dev.newseladev.com' : process.env.MONOLITH_BASE_URL;

const LANGSTON_SEARCH_STANDARDS_ENDPOINT = `${MONOLITH_BASE_URL}/api/v3/content/delivery/metadata/search/`;

const StandardsSelector = ({ data, parent }) => {
  const [activeTab, setActiveTab] = useState(TABS.SEARCH);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [appliedStandards, setAppliedStandards] = useState([]);

  const isSaving = useStoreState((state) => state.saveStatus.isSaving);

  // Apollo does not update the loading state upon refetch.
  const [isRefetchingRecommended, setIsRefetchingRecommended] = useState(false);

  const [state, dispatch] = useReducer(reducer, initialState);
  const [modalProps, setModalActive, triggerRef] = useModal(false);
  const [showRecommendations, setShowRecommendations] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState(state.search);

  // Added fallback for unit testing
  const debounceTimeout = useRef();

  const contentQueryId = data?.id;

  const [isStandardsComplete, setIsStandardsComplete] = useState(false);

  const {
    data: fullContentData,
    loading: fullContentLoading,
    refetch: refetchContentData
  } = useQuery(getContentQuery(data), {
    variables: { id: contentQueryId },
    skip: !contentQueryId,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only'
  });

  const content = (QUESTION_TYPES.includes(data.questionType) ? fullContentData?.assessmentQuestion : fullContentData?.content) || {};
  const fetchUntilComplete = useRef();
  const previousCountRef = useRef(-1);

  fetchUntilComplete.current = async (attempt = 0) => {
    if (attempt > 10) {
      setIsStandardsComplete(true);
      return;
    }

    if (!refetchContentData) {
      setIsStandardsComplete(true);
      return;
    }

    const result = await refetchContentData({ id: contentQueryId });
    const updatedContent = result.data?.content || {};
    const standardsCount = updatedContent.metadataStandards?.length ?? 0;

    // Check if count hasn't changed from previous attempt (2 consecutive same counts)
    if (standardsCount === previousCountRef.current) {
      setIsStandardsComplete(true);
      return;
    }

    previousCountRef.current = standardsCount;
    setTimeout(() => fetchUntilComplete.current(attempt + 1), 500);
  };

  useEffect(() => {
    if (fullContentData && !fullContentLoading && !isStandardsComplete && contentQueryId && refetchContentData) {
      fetchUntilComplete.current(0);
    }
  }, [fullContentData, fullContentLoading, isStandardsComplete, contentQueryId, refetchContentData]);

  const {
    data: recommendedStandards,
    loading: recommendedLoading,
    error: recommendedError,
    refetch: refetchRecommended,
  } = useRecommendations({ content, parent });

  const { search, ...stateWithoutSearch } = state;
  const queryJson = useMemo(() => {
    const appliedStandardIds = appliedStandards.map((standard) => standard.id);
    return generateQueryJson({ ...stateWithoutSearch, search: debouncedSearch }, appliedStandardIds);
  }, [appliedStandards, stateWithoutSearch, debouncedSearch]);

  const shouldFetch = !!state.search && state.search.trim().length > 0;

  const {
    data: standardsSearchData,
    loading: standardsSearchLoading,
    error: standardsSearchError,
  } = useRestQuery(
    shouldFetch ? LANGSTON_SEARCH_STANDARDS_ENDPOINT : null
    , {
      method: 'POST',
      body: JSON.stringify(queryJson),
      headers: { 'Content-Type': 'application/json' }
    }
  );

  const hasSearch = state.search.trim().length > 0;

  const handleBackToRecommendations = () => setShowRecommendations(true);

  const categorizedStandards = sortCategorizedStandards([...appliedStandards, ...selectedStandards].map((standard) => {
    const isApplied = appliedStandards.some((s) => s.id === standard.id);
    const recommendedMeta = recommendedStandards?.predictedStandards?.find((rec) => rec.id === standard.id);

    return {
      ...standard,
      __temporaryStatus: isApplied ? STANDARD_APPLIED : STANDARD_SELECTED,
      confidenceRating: recommendedMeta?.confidenceRating ?? null,
    };
  }));

  const filteredRecommendations = sortByConfidenceRating(
    recommendedStandards?.predictedStandards?.filter(
      // Use backend-applied standards (not local state) to avoid showing already-applied items
      // even if the user temporarily "soft-deletes" them from the modal session.
      (rec) => !content?.metadataStandards?.some((app) => app.id === rec.id)
    ) || []
  );

  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(state.search);
      if (state.search.trim().length > 0) {
        setShowRecommendations(false);
      }
    }, 800);
    return () => clearTimeout(debounceTimeout.current);
  }, [state.search]);

  useEffect(() => {
    if (recommendedStandards && content.metadataStandards?.length === 0 && modalProps.isActive) {
      const PRESELECTED_THRESHOLD = 50;
      const preselectedStandards = recommendedStandards.predictedStandards.filter(
        (standard) => standard.confidenceRating >= PRESELECTED_THRESHOLD
      );
      setSelectedStandards(preselectedStandards);
    }
  }, [recommendedStandards, content.metadataStandards, modalProps.isActive]);

  if (recommendedError) {
    toast.error('Error loading recommended standards');
  }

  const handleOpen = () => {
    setSelectedStandards([]);
    setAppliedStandards(
      content?.metadataStandards || []
    );
    setModalActive(true);
  };

  const handleClose = ({ applyAndResetCache = false } = {}) => {
    if (applyAndResetCache && refetchContentData) {
      refetchContentData({ id: contentQueryId });
      setIsRefetchingRecommended(true);
      // We don't need to wait refetch to happen to close the modal.
      refetchRecommended({ uid: content?.uid }).then(() => {
        setIsRefetchingRecommended(false);
      });
    }
    dispatch({ type: CLEAR_ALL_FILTERS });
    setSelectedStandards([]);
    setActiveTab(TABS.SEARCH);
    setModalActive(false);
  };

  const handleSearchInput = (value) => {
    setShowRecommendations(false);
    dispatch({ type: SET_SEARCH, payload: value });
  };

  const renderStandards = () => {
    function renderRecommendations () {
      if (recommendedLoading || isRefetchingRecommended) return <LoadingShimmer />;
      if (!shouldFetchPredictions(content)) {
        return (
          <div css={$conceptsNotFound}>
            <img src={image} alt='No results found' />
            <p>Tag Concepts in order to see standard recommendations</p>
          </div>
        );
      }
      if (filteredRecommendations?.length === 0) {
        return (
          <div css={$conceptsNotFound}>
            <img src={image} alt='No results found' />
            <p>No recommendations found. Select more concepts to get better results.</p>
          </div>
        );
      }
      return (
        <StandardsList
          selectedStandards={selectedStandards}
          standards={filteredRecommendations}
          isSearching={false}
          onBackToRecommendations={handleBackToRecommendations}
          setSelectedStandards={setSelectedStandards}
          activeTab={activeTab}
          categorizedStandards={categorizedStandards}
        />
      );
    }

    function renderSearchResults () {
      if (standardsSearchLoading) return <LoadingShimmer />;
      if (standardsSearchError) {
        return (
          <div css={$conceptsNotFound}>
            <img src={image} alt='No results found' />
            <p>Error searching standards. Please try again.</p>
          </div>
        );
      }
      if (!standardsSearchData || !Array.isArray(standardsSearchData.results) || standardsSearchData.results.length === 0) {
        return (
          <div css={$conceptsNotFound}>
            <img src={image} alt='No results found' />
            <p>No standards found for your search.</p>
          </div>
        );
      }
      return (
        <StandardsList
          selectedStandards={selectedStandards}
          standards={standardsSearchData.results}
          isSearching
          onBackToRecommendations={handleBackToRecommendations}
          setSelectedStandards={setSelectedStandards}
          activeTab={activeTab}
          categorizedStandards={categorizedStandards}
        />
      );
    }

    // Since we only refetch predictions after content changes, we need to wait for the new content to be loaded.
    if (recommendedLoading || isRefetchingRecommended || isSaving) return <LoadingShimmer />;
    if (!shouldFetchPredictions(content) && !hasSearch) {
      return (
        <div css={$conceptsNotFound}>
          <img src={image} alt='No results found' />
          <p>Tag Concepts in order to see standard recommendations</p>
        </div>
      );
    }
    if (showRecommendations) return renderRecommendations();

    if (hasSearch) {
      return renderSearchResults();
    }

    return renderRecommendations();
  };

  const renderModalContent = () => (
    <div css={$modalContentStyles}>
      <Header activeTab={activeTab} setActiveTab={setActiveTab} handleClose={handleClose} categorizedStandards={categorizedStandards} />
      <Divider />
      <div css={$modalBody(activeTab)}>
        {activeTab === TABS.SEARCH ? (
          <>
            <Filters state={state} dispatch={dispatch} handleSearchInput={handleSearchInput} />
            {renderStandards()}
          </>
        ) : (
          <ReviewContainer categorizedStandards={categorizedStandards} setSelectedStandards={setSelectedStandards} setAppliedStandards={setAppliedStandards} />
        )}
      </div>
      {!showRecommendations && hasSearch && activeTab === TABS.SEARCH && (
        <Pagination
          currentPage={state.page}
          pageSize={state.pageSize}
          total={standardsSearchData?.total_result_count || 0}
          onPageChange={(page) => dispatch({ type: SET_PAGE, payload: page })}
          onPageSizeChange={({ selectedItem }) => {
            dispatch({ type: SET_PAGE_SIZE, payload: selectedItem.value });
            dispatch({ type: SET_PAGE, payload: 0 });
          }}
        />
      )}
      <Divider />
      <Footer
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        handleClose={handleClose}
        content={content}
        selectedStandards={selectedStandards}
        appliedStandards={appliedStandards}
      />
    </div>
  );

  return (
    <>
      <Button onClick={handleOpen} ref={triggerRef} disabled={!isStandardsComplete}>
        Manage Standards
      </Button>
      <Modal
        {...modalProps} onBackgroundClick={handleClose} labelId='modal_title' __cssFor={{
          root: {
            zIndex: Z_INDEX_9999_MAX

          }
        }}
        onClose={handleClose}
      >
        {renderModalContent()}
      </Modal>
    </>
  );
};
StandardsSelector.propTypes = {
  data: PropTypes.object,
  parent: PropTypes.object,
};

export default StandardsSelector;
